import { useErrorLogService } from 'ErrorLogging';
import { useEffect, useState } from 'react';
import { type TimelineEvent } from './ProjectFieldsModal.types';
import { formatSimilarityAndOriginalityStatus } from './projectFieldsOptions';

// Fetches the project_progress change timeline from GET /api/project_progress_timelines/:id.json.
// It applies the same S&O labels as the legacy directive. It refetches when the progress changes.
export function useProjectTimeline(projectProgressId: string | null, updatedAt: number | null): TimelineEvent[] {
    const errorLogService = useErrorLogService();
    const [events, setEvents] = useState<TimelineEvent[]>([]);

    useEffect(() => {
        if (!projectProgressId) {
            setEvents([]);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(`/api/project_progress_timelines/${projectProgressId}.json`);
                if (!response.ok) throw new Error('Failed to load timeline');
                const data = await response.json();
                // The controller renders through render_records, so the events sit under
                // contents.project_progress_timelines[0].
                const rawEvents: TimelineEvent[] = data?.contents?.project_progress_timelines?.[0]?.events || [];
                if (cancelled) return;
                setEvents(
                    rawEvents.map(event => ({
                        ...event,
                        changes: (event.changes || []).map(change =>
                            change.attr === 'Similarity & Originality Status'
                                ? {
                                      ...change,
                                      from: formatSimilarityAndOriginalityStatus(change.from) ?? null,
                                      to: formatSimilarityAndOriginalityStatus(change.to) ?? null,
                                  }
                                : change,
                        ),
                    })),
                );
            } catch (error) {
                if (!cancelled) {
                    errorLogService?.notify('Failed to load project progress timeline', error, {
                        level: 'warning',
                        project_progress_id: projectProgressId,
                    });
                    setEvents([]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [projectProgressId, updatedAt, errorLogService]);

    return events;
}
